import React, { useEffect } from "react";
import { InjectedFormProps, reduxForm, getFormValues, SubmissionError, isValid } from "redux-form";
import { BankAccountChildProps } from "../../types/props";
import i18n from "../../i18n/config";
import { FORM_NAMES } from "../../constants/forms";
import { BANK_ACCOUNT_FLOW_TYPES, BANK_ACCOUNT_FORM_FIELDS, FIELD_TYPES } from "../../constants/enums";
import { useHistory } from "react-router-dom";
import { ROUTES } from "../../constants/clientRoutes";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../types/redux";

import DynamicInputField from "../../components/Common/DynamicFields/DynamicInputField";
import { BankAccountFormValues } from "../../types/forms";
import { MoneyTransferPage } from "../../types/moneyTransfer";
import BankAccountsApi from "../../api/bankAccounts";
import { CountryType, CurrencyType } from "../../types/api/catalog";
import { validateBankAccountEmailAddress, validateOptionalEmail } from "../../services/customValidations";
import { ResetBankAccountState, UpdateBankAccountFormFields } from "../../actions/bankAccounts";
import useErrorModal from "../../hooks/useErrorModal";

const CommunicationPreferences: React.FC<BankAccountChildProps & InjectedFormProps<{}, BankAccountChildProps>> = (props) => {
  const { setTitle, setSubTitle, setForwardButton } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const openErrorModal = useErrorModal();
  const BENEFICIARY_PAGE_ID = "beneficiary";

  const formValues = useSelector((state: RootState) => getFormValues(FORM_NAMES.BANK_ACCOUNTS)(state)) as BankAccountFormValues;
  const valid = useSelector((state: RootState) => isValid(FORM_NAMES.BANK_ACCOUNTS)(state));
  const { destinationCurrencyCode, availableCurrencies, type, allocationId } = useSelector((state: RootState) => state.bankAccounts);
  const { countries } = useSelector((state: RootState) => state.catalog);

  const forwardButtonInitialState = {
    text: i18n.t("COMMON.SUBMIT"),
    clickFunc: () => props.handleSubmit(onSubmit)(),
    disabled: false
  };

  useEffect(() => {
    const title = type === BANK_ACCOUNT_FLOW_TYPES.NEW ? i18n.t("PAGES.BANK_ACCOUNTS.COMMUNICATION_PREFERENCES.ADD_NEW_TITLE") : i18n.t("PAGES.BANK_ACCOUNTS.COMMUNICATION_PREFERENCES.EDIT_TITLE");
    setTitle(title);
    setSubTitle(i18n.t("PAGES.BANK_ACCOUNTS.COMMUNICATION_PREFERENCES.SUBTITLE"));
    setForwardButton(forwardButtonInitialState);

  }, []);

  useEffect(() => {
    setForwardButton({ ...forwardButtonInitialState, disabled: !valid });
  }, [valid]);

  useEffect(() => {
    setForwardButton({ ...forwardButtonInitialState, loading: props.submitting });
  }, [props.submitting]);

  const onSubmit = async () => {
    const requestBody = mapFormValuesToRequestBody();
    try {
      if (type === BANK_ACCOUNT_FLOW_TYPES.EDIT) {
        await BankAccountsApi.UpdateBankAccount(requestBody, allocationId);
      } else {
        await BankAccountsApi.CreateBankAccount(requestBody);
      }
      history.push(ROUTES.BANKS_ACCOUNTS.CONFIRMED);
      dispatch(ResetBankAccountState(type));
    } catch (err) {
      if (err?.data?.result?.pages) {
        const { errorObject, pageWithErrors } = getErrorFromWorkflow(err.data.result.pages);
        dispatch(UpdateBankAccountFormFields(err.data.result.pages));
        const url = pageWithErrors === "beneficiary" ? ROUTES.BANKS_ACCOUNTS.BENEFICIARY_DETAILS : ROUTES.BANKS_ACCOUNTS.BANK_DETAILS;
        history.push(url);
        throw new SubmissionError({ ...errorObject });
      } else {
        openErrorModal(err);
      }
    }
  }

  const mapFormValuesToRequestBody = () => {
    const requestBody: { fields: { serviceProviderFieldId: string; value: any }[] } = {
      fields: []
    };
    const fields = Object.keys(formValues);

    fields.forEach((field: string) => {
      if (formValues[field]) {
        let value = formValues[field];

        // If phone number field, send string 
        if (field === BANK_ACCOUNT_FORM_FIELDS.BENEFICIARY_PHONE_NUMBER) {
          value = value.country.phoneCode + value.number;
        }

        requestBody.fields.push({
          serviceProviderFieldId: field,
          value
        });
      }
    });

    requestBody.fields.push({
      serviceProviderFieldId: "destinationCountryCode",
      value: countries.find((country: CountryType) => country.id === formValues[BANK_ACCOUNT_FORM_FIELDS.DESTINATION_COUNTRY])?.iso
    });

    requestBody.fields.push({
      serviceProviderFieldId: "destinationCurrencyCode",
      value: destinationCurrencyCode
    });

    requestBody.fields.push({
      serviceProviderFieldId: "destinationCurrencyId",
      value: availableCurrencies.find((currency: CurrencyType) => currency.code === destinationCurrencyCode)?.id
    })

    return requestBody;
  }

  /**
   * Collects the first validation error message per field across all pages.
   */
  const collectFieldErrors = (pages: MoneyTransferPage[] = []): Record<string, string> => {
    const errors: Record<string, string> = {};

    for (const page of pages) {
      for (const section of page.sections ?? []) {
        for (const field of section.fields ?? []) {
          const msg = field.validationError?.[0]?.errorMessage;
          if (msg) {
            errors[field.serviceProviderFieldId] = msg;
          }
        }
      }
    }

    return errors;
  }

  /**
   * Determines which page should be navigated to when errors exist.
   * if errors exist on the beneficiary page, that page takes precedence. Otherwise, the first page with errors is returned.
   */
  const determineErrorPage = (pages: MoneyTransferPage[] = []) => {
    let pageWithErrors: string | null = null;

    for (const page of pages) {
      const hasErrors = (page.sections ?? []).some(section =>
        (section.fields ?? []).some(field => (field.validationError?.length ?? 0) > 0)
      );

      if (hasErrors) {
        if (page.id === BENEFICIARY_PAGE_ID) {
          pageWithErrors = BENEFICIARY_PAGE_ID;
          break;
        } else {
          pageWithErrors = page.id;
        }
      }
    }

    return pageWithErrors;
  }

  /**
   * Gets errors from the workflow object based on the category passed in.
   */
  const getErrorFromWorkflow = (pages: MoneyTransferPage[] = []) => {
    return {
      errorObject: collectFieldErrors(pages),
      pageWithErrors: determineErrorPage(pages),
    };
  }

  return (
    <form onSubmit={props.handleSubmit(onSubmit)}>
      <div className="max-w-360">
        <DynamicInputField
          id={BANK_ACCOUNT_FORM_FIELDS.EMAIL_ADDRESS}
          type={FIELD_TYPES.TEXT}
          label={i18n.t("FORMS.COMMON.EMAIL_ADDRESS")}
          required={false}
          focus={true}
          validate={validateOptionalEmail}
        />

        <DynamicInputField
          id={BANK_ACCOUNT_FORM_FIELDS.CONFIRM_EMAIL_ADDRESS}
          type={FIELD_TYPES.TEXT}
          label={i18n.t("FORMS.COMMON.CONFIRM_EMAIL_ADDRESS")}
          required={false}
          focus={true}
          validate={validateBankAccountEmailAddress}
        />

        <p>{i18n.t("PAGES.BANK_ACCOUNTS.COMMUNICATION_PREFERENCES.P1")}</p>
      </div>
    </form>
  );
}

export default reduxForm<{}, BankAccountChildProps>({
  form: FORM_NAMES.BANK_ACCOUNTS,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true
})(CommunicationPreferences);